import { Stack } from '@mui/material';
import { get, isArray } from 'lodash';
import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingBackdrop from 'src/components/ui-component/loading/LoadingBackdrop';
import MuiSnackbar from 'src/components/ui-component/mui-snackbar/MuiSnackbar';
import MUIToolTip from 'src/components/ui-component/mui-tooltip/MUIToolTip';
import { useAuthentication } from 'src/context/AuthContext';
import { PostRecord } from 'src/lib/api/PostRecord';
import { APIS } from 'src/lib/constants/apis';
import {
	ANTRAG_BUTTON_NAME,
	ANTRAGS_STATUS,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	MITTEL_ABRUF_ZUERST_ERSTELLEN_TEXT,
	NACHWEIS_POSITION_STATUS,
	NOCH_NICHT_MOEGLICH_TEXT,
	VN_ERSTELLUNG_NOCH_NICHT_MOEGLICH_TEXT,
} from 'src/lib/constants/constant';
import { PAGES_URLS } from 'src/lib/constants/routes';
import { enumT } from 'src/utils/helper';
import { AntragButton, AntragsButtonWrapper, getMonatListe } from '../FoerderungUebersicht';

// Import Schema
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import MittelabrufSchema from 'src/lib/paths/mittelabruf_FULL_Paths.json';
import {
	getMittelabrufBankdatenFromLastMittelabrufOrAntrag,
	getMittelabrufeFromProjektJahr,
	getMittelabrufErklaerungenUndAnlagenData,
} from 'src/utils/dashboardHelper';
const P = Schema.attributes;
const MA_P = MittelabrufSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const KreativwirtschaftDashboard: FC<{ application: any }> = ({ application }) => {
	const { authenticatedData } = useAuthentication();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [wsError, setWsError] = useState(false);

	const mittelabruf = get(application, P.Foerderantrag_Mittelabrufs.path, []);
	const verwendungsnachweis = get(application, P.Foerderantrag_Verwendungsnachweiss.path, []);
	const abschlussbericht = get(application, P.Foerderantrag_Abschlussberichts.path, []);

	const projektJahr = get(application, P.Allgemein_Projektstart_Jahr.path, new Date().getFullYear());

	const hatVN = isArray(verwendungsnachweis) && verwendungsnachweis.length > 0;
	const vnStatus = hatVN ? get(verwendungsnachweis[0], 'attributes.Verwendungsnachweisstatus_Status', '') : '';
	const alleMAsFreigegeben =
		isArray(mittelabruf) &&
		mittelabruf.length > 0 &&
		mittelabruf.every((ma: any) => get(ma, MA_P.Mittelabrufstatus_Mittelabrufstatus.path) === ANTRAGS_STATUS.freigegeben);
	const letzterMA = isArray(mittelabruf) && mittelabruf.length > 0 ? mittelabruf[mittelabruf.length - 1] : null;

	const handleCreateMittelabruf = async () => {
		const foerderProgramm = get(application, P.Foerderprogramm.path);
		const erklaerungenUndAnlagenData = getMittelabrufErklaerungenUndAnlagenData(foerderProgramm);
		const mittelabrufeImProjektJahr = getMittelabrufeFromProjektJahr(mittelabruf, projektJahr);
		const summeVorangegangenerMittelabrufeImHaushaltsjahr = mittelabrufeImProjektJahr.reduce(
			(acc: number, curr: any) => acc + (Number(get(curr, MA_P.Ermittlung_Des_Auszahlungsbetrages_Bereits_Ausgezahlte_Mittel.path, 0)) || 0),
			0,
		);
		const MA_Data = {
			Foerderantrag: get(application, 'id'),
			Mittelabrufstatus_Mittelabrufstatus: ANTRAGS_STATUS.inBearbeitung,
			Allgemein_Haushaltsjahr: projektJahr,
			Allgemein_Abrufdatum: new Date(),
			// Bankdaten
			...getMittelabrufBankdatenFromLastMittelabrufOrAntrag(application),
			Ermittlung_Des_Auszahlungsbetrages_Bewilligte_Zuwendung: get(application, P.Bewilligung_Bewilligte_Zuwendung.path, 0),
			Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr: summeVorangegangenerMittelabrufeImHaushaltsjahr,
			Kalkulation_Block_1: [
				{
					Zelle_1: get(application, P.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation.path, ''),
					Zelle_2: get(application, P.Orga_Foerderquote_Gesamt.path),
				},
			],
			...erklaerungenUndAnlagenData,
		};
		try {
			setLoading(true);
			let res = await PostRecord(APIS.Mittelabruf, { data: MA_Data });
			if (res) {
				try {
					const nachweisKombinationen = [
						{ status: NACHWEIS_POSITION_STATUS.bereitsAngefalleneAusgaben, kostenart: KOSTEN_ARTEN.Personalkosten },
						{ status: NACHWEIS_POSITION_STATUS.geplant, kostenart: KOSTEN_ARTEN.Personalkosten },
						{ status: NACHWEIS_POSITION_STATUS.bereitsAngefalleneAusgaben, kostenart: KOSTEN_ARTEN.Sachkosten },
						{ status: NACHWEIS_POSITION_STATUS.geplant, kostenart: KOSTEN_ARTEN.Sachkosten },
					];
					for (const { status, kostenart } of nachweisKombinationen) {
						await PostRecord(APIS.Nachweisposition, {
							data: {
								Mittelabruf: get(res, 'data.id'),
								Allgemein_Status: status,
								Allgemein_Kostentraeger: KOSTENTRAEGER.Antragsteller,
								Allgemein_Unternehmen_Institution: get(authenticatedData, 'organisationId'),
								Allgemein_Kostenart: KOSTEN_POSITION.Ausgabe,
								Allgemein_Kostenart_Finanzierung: kostenart,
								Allgemein_Bezeichnung: '',
							},
						});
					}
				} catch (err: any) {
					setWsError(true);
					console.error('ERROR: Creating Verwendungsnachweis');
				} finally {
					navigate(`${PAGES_URLS.OnlineAntrag}`, {
						state: {
							id: get(res, 'data.id'),
							api: APIS.Mittelabruf,
							mittelabrufIndex: mittelabruf.length + 1,
						},
					});
				}
			}
		} catch (err: any) {
			setWsError(true);
			console.error('ERROR: Creating Mittelabruf ', err);
		} finally {
			setLoading(false);
		}
	};
	const handleCreateVerwendungsnachweis = async (currentMittelAbrufId: number) => {
		const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);
		const VN_DATA = {
			Allgemein_Haushaltsjahr: projektJahr,
			Verwendungsnachweisstatus_Status: ANTRAGS_STATUS.inBearbeitung,
			Foerderantrag: get(application, 'id'),
			Mittelabruf: currentMittelAbrufId,
			Allgemein_Foerderquote_Antragstellerin: get(application, P.Antragstellende_Organisation_Foerderquote.path, []).find(
				(ele: any) => get(ele, 'Projektjahr') === projektJahr,
			)?.Foerderquote_403741,
		};

		try {
			setLoading(true);
			const vnRes = await PostRecord(APIS.Verwendungsnachweis, { data: VN_DATA });
			if (vnRes) {
				for (let i = 0; i < allKostenPosition.length; i++) {
					const eachKP = allKostenPosition[i];
					const NW_DATA = {
						Allgemein_Nr: i + 1,
						Allgemein_Kostentraeger: get(eachKP, KP_P.Allgemein_Kostentraeger.path),
						Allgemein_Unternehmen_Institution: get(eachKP, `${KP_P.Unternehmen.path}.id`),
						Verwendungsnachweis: get(vnRes, 'data.id'),
						Allgemein_Kostenposition: get(eachKP, 'id'),
						Allgemein_Kostenart: get(eachKP, KP_P.Allgemein_Kostenposition.path),
						Allgemein_Bezeichnung: get(eachKP, KP_P.Allgemein_Bezeichnung.path),
						Kostennachweise_Nachweise: [{ Kommentar: '' }],
						Stundennachweis_Geleistete_Stunden: getMonatListe(eachKP),
					};
					await PostRecord(APIS.Nachweisposition, { data: NW_DATA });
				}

				navigate(PAGES_URLS.OnlineAntrag, { state: { id: get(vnRes, 'data.id'), api: APIS.Verwendungsnachweis } });
			}
		} catch (err: any) {
			console.error('ERROR: Creating Verwendungsnachweis ', err);
		} finally {
			setLoading(false);
		}
	};

	const handleCreateAbschlussbericht = async () => {
		let ABData = {
			Foerderantrag: get(application, 'id'),
			Abschlussberichtstatus_Abschlussberichtsstatus: ANTRAGS_STATUS.inBearbeitung,
			Antragsteller: get(application, `${P.Antragstellende_Organisation.path}.id`),
		};

		try {
			setLoading(true);
			let res = await PostRecord(APIS.Abschlussbericht, { data: ABData });
			if (res) navigate(PAGES_URLS.OnlineAntrag, { state: { id: get(res, 'data.id'), api: APIS.Abschlussbericht } });
		} catch (err: any) {
			setWsError(true);
			console.error('ERROR: Creating Abschlussbericht ', err);
		} finally {
			setLoading(false);
		}
	};

	const RenderMittelabrufButton: FC<{}> = () => {
		if (isArray(mittelabruf) && mittelabruf.length > 0) {
			const letzterMAFreigegeben =
				get(mittelabruf[mittelabruf.length - 1], MA_P.Mittelabrufstatus_Mittelabrufstatus.path) === ANTRAGS_STATUS.freigegeben;
			const kannNeuenMAErstellen = letzterMAFreigegeben && !hatVN;

			return (
				<AntragsButtonWrapper title='Mittelabruf:'>
					{mittelabruf.map((eachMA: any, i: number) => {
						return (
							<AntragButton
								key={i}
								name={`Mittelabruf #${i + 1}`}
								description={enumT(get(eachMA, MA_P.Mittelabrufstatus_Mittelabrufstatus.path, ''))}
								onClick={() =>
									navigate(PAGES_URLS.OnlineAntrag, {
										state: {
											id: get(eachMA, 'id'),
											api: APIS.Mittelabruf,
											mittelabrufIndex: i + 1,
										},
									})
								}
							/>
						);
					})}
					{get(application, P.Antragstatus_Antragstatus.path, '') === ANTRAGS_STATUS.inFoerderung && !hatVN && (
						<MUIToolTip
							title='Sie können neuen Mittelabruf erstellen, sobald die vorherige Mittelabruf freigegeben ist'
							showTootip={!kannNeuenMAErstellen}
						>
							<AntragButton
								name='Mittelabruf erstellen'
								description=''
								onClick={() => handleCreateMittelabruf()}
								variant={'contained'}
								disabled={!kannNeuenMAErstellen}
							/>
						</MUIToolTip>
					)}
				</AntragsButtonWrapper>
			);
		} else {
			if (get(application, P.Antragstatus_Antragstatus.path, '') === ANTRAGS_STATUS.inFoerderung) {
				return (
					<AntragsButtonWrapper title='Mittelabruf:'>
						<AntragButton name='Mittelabruf' description='Mittelabruf erstellen' onClick={handleCreateMittelabruf} variant='text' />
					</AntragsButtonWrapper>
				);
			} else {
				return (
					<AntragsButtonWrapper title={'Mittelabruf:'}>
						<AntragButton name={NOCH_NICHT_MOEGLICH_TEXT} description='offen' onClick={() => {}} disabled={true} />
					</AntragsButtonWrapper>
				);
			}
		}
	};
	const RenderVerwendungsnachweisButton: FC<{}> = () => {
		if (hatVN) {
			// VN existiert bereits → Button zum Ansehen
			return (
				<AntragsButtonWrapper title='Verwendungsnachweis:'>
					<AntragButton
						name='Verwendungsnachweis'
						description={enumT(vnStatus)}
						onClick={() =>
							navigate(PAGES_URLS.OnlineAntrag, {
								state: {
									id: get(verwendungsnachweis[0], 'id'),
									api: APIS.Verwendungsnachweis,
								},
							})
						}
					/>
				</AntragsButtonWrapper>
			);
		} else if (alleMAsFreigegeben && letzterMA) {
			// VN-Erstellung temporär für alle Programme deaktiviert, bis der VN im Portal umgesetzt ist.
			return (
				<AntragsButtonWrapper title='Verwendungsnachweis:'>
					<MUIToolTip title={VN_ERSTELLUNG_NOCH_NICHT_MOEGLICH_TEXT} showTootip={true}>
						<AntragButton
							name='Verwendungsnachweis erstellen'
							description=''
							onClick={() => handleCreateVerwendungsnachweis(get(letzterMA, 'id'))}
							variant={'contained'}
							disabled={true}
						/>
					</MUIToolTip>
				</AntragsButtonWrapper>
			);
		} else {
			// Noch nicht möglich
			const tooltipText =
				isArray(mittelabruf) && mittelabruf.length > 0 ?
					'Alle Mittelabrufe müssen freigegeben sein, bevor ein Verwendungsnachweis erstellt werden kann'
				:	MITTEL_ABRUF_ZUERST_ERSTELLEN_TEXT;
			return (
				<AntragsButtonWrapper title='Verwendungsnachweis:'>
					<MUIToolTip title={tooltipText}>
						<AntragButton name='Verwendungsnachweis' description='erstellen' onClick={() => {}} disabled={true} />
					</MUIToolTip>
				</AntragsButtonWrapper>
			);
		}
	};
	const RenderAbschlussberichtButton: FC<{}> = () => {
		if (isArray(abschlussbericht) && abschlussbericht.length > 0) {
			return (
				<AntragsButtonWrapper title='Abschlussbericht:'>
					{abschlussbericht.map((eachAbschlussbericht: any, i: number) => {
						return (
							<AntragButton
								key={i}
								name='Abschlussbericht'
								description='Ansehen'
								onClick={() =>
									navigate(`${PAGES_URLS.OnlineAntrag}`, {
										state: {
											id: get(eachAbschlussbericht, 'id'),
											api: APIS.Abschlussbericht,
										},
									})
								}
							/>
						);
					})}
				</AntragsButtonWrapper>
			);
		} else {
			const vnFreigegeben = vnStatus === ANTRAGS_STATUS.freigegeben;
			const projektBeendet = get(application, P.Antragstatus_Antragstatus.path, '') === ANTRAGS_STATUS.ProjektBeendet;
			if (vnFreigegeben || projektBeendet) {
				return (
					<AntragsButtonWrapper title='Abschlussbericht:'>
						<AntragButton
							name='Abschlussbericht'
							description='Abschlussbericht erstellen'
							onClick={handleCreateAbschlussbericht}
							variant='text'
						/>
					</AntragsButtonWrapper>
				);
			} else {
				return (
					<AntragsButtonWrapper title='Abschlussbericht:'>
						<AntragButton name={NOCH_NICHT_MOEGLICH_TEXT} description='offen' onClick={() => {}} variant='text' disabled={true} />
					</AntragsButtonWrapper>
				);
			}
		}
	};

	return (
		<>
			<LoadingBackdrop open={loading} />
			<MuiSnackbar showSnackbar={wsError} closeSnackbar={() => setWsError(false)} alertMessage='Etwas ist schief gelaufen' />
			<AntragsButtonWrapper title='Antrag:'>
				<AntragButton
					name={get(application, P.Allgemein_Projekttitel.path) ? get(application, P.Allgemein_Projekttitel.path) : ANTRAG_BUTTON_NAME}
					description={enumT(get(application, P.Antragstatus_Antragstatus.path))}
					onClick={() =>
						navigate(PAGES_URLS.OnlineAntrag, {
							state: {
								id: get(application, 'id'),
								api: APIS.Antrag,
								antragsformular: get(application, P.Allgemein_Antragsformular.path),
							},
						})
					}
				/>
			</AntragsButtonWrapper>

			<Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent={'space-between'}>
				<span>
					<RenderMittelabrufButton />
				</span>
				<span>
					<RenderVerwendungsnachweisButton />
				</span>
			</Stack>

			<RenderAbschlussberichtButton />
		</>
	);
};

export default KreativwirtschaftDashboard;
